<template>
  <Popover class="fixed top-0 left-0 right-0 z-50 bg-gray-50 transition-shadow duration-200 border-b border-gray-50" :class="{ 'shadow-md': scrolled }">
    <div class="max-w-7xl mx-auto px-4 sm:px-6">
      <div class="flex justify-between items-center py-6">
        <div class="flex-shrink-0">
          <router-link to="/">
            <span class="sr-only">Workflow</span>
            <img class="h-8 w-auto sm:h-10" :src="logoHorizontal" alt="" />
          </router-link>
        </div>
        
        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex flex-1 justify-end space-x-10">
          <router-link 
            v-for="item in navigation" 
            :key="item.name" 
            :to="item.to" 
            class="text-base font-medium text-gray-500 hover:text-gray-900"
            :class="{ 'text-awtmtn-blue font-semibold': route.path === item.to }"
          >
            {{ item.name }}
            <div 
              class="h-0.5 bg-awtmtn-blue transform scale-x-0 transition-transform duration-200"
              :class="{ '!scale-x-100': route.path === item.to }"
            ></div>
          </router-link>
        </nav>
        
        <!-- Mobile Menu Button -->
        <div class="flex lg:hidden">
          <button @click="mobileMenuOpen = !mobileMenuOpen" type="button" class="bg-gray-50 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
            <span class="sr-only">Open menu</span>
            <font-awesome-icon icon="bars" class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition
      enter-active-class="duration-150 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="duration-100 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="mobileMenuOpen" class="fixed inset-0 z-40">
        <!-- Overlay -->
        <div class="fixed inset-0 bg-black bg-opacity-50" @click="mobileMenuOpen = false"></div>
        <!-- Menu -->
        <div class="absolute top-0 inset-x-0 p-2 transition transform origin-top-right lg:hidden z-50">
          <div class="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50">
            <div class="pt-5 pb-6 px-5">
              <div class="flex items-center justify-between">
                <div>
                  <img class="h-8 w-auto" :src="logoHorizontal" alt="" />
                </div>
                <div class="-mr-2">
                  <button @click="mobileMenuOpen = false" type="button" class="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    <span class="sr-only">Close menu</span>
                    <font-awesome-icon icon="xmark" class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div class="mt-6">
                <nav class="grid gap-y-8">
                  <router-link
                    v-for="item in navigation"
                    :key="item.name"
                    :to="item.to"
                    class="text-base font-medium text-gray-900 hover:text-gray-700"
                    :class="{ 'text-awtmtn-blue font-semibold': route.path === item.to }"
                    @click="mobileMenuOpen = false"
                  >
                    {{ item.name }}
                  </router-link>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Popover>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { Popover } from '@headlessui/vue'
import navigationData from '../data/navigation.json'
import logoHorizontal from '../assets/svg/logo-horizontal.svg'

interface NavigationItem {
  name: string;
  to: string;
  icon: string;
}

const navigation = navigationData.navigation as NavigationItem[]
const route = useRoute()
const mobileMenuOpen = ref(false)
const scrolled = ref(false)

const handleScroll = () => {
  scrolled.value = window.scrollY > 0
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style>
.z-40 {
  z-index: 40;
}

.z-50 {
  z-index: 50;
}

.shadow-md {
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Add a pseudo-element for continuous background */
.fixed {
  position: fixed;
}

.fixed::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 1px;
  background-color: rgb(249, 250, 251); /* bg-gray-50 color */
}
</style>